import {
  forwardRef,
  useMemo,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from '@data/utils/cn/cn';
import {
  FormattedNumber,
  type FormattedNumberProps,
} from '@data/components/basic/FormattedNumber/FormattedNumber';
import { Tooltip } from '@scholastic/mockingjay';
import InfoIcon from '@data/assets/info-icon.svg';

const labeledNumberContainer = cva('act-antialiased flex', {
  variants: {
    labelOrientation: {
      top: 'flex-col',
      right: 'flex-row gap-1',
      bottom: 'flex-col',
      left: 'flex-row gap-1',
      none: '',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-sm laptop:text-lg',
      lg: 'text-xl',
    },
    align: {
      left: 'items-start',
      center: 'items-center',
      right: 'items-end',
    },
    spacing: {
      none: '',
      full: 'justify-between',
    },
    strong: {
      true: '',
      false: '',
    },
    card: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      size: 'xs',
      card: false,
      className: 'text-xs',
    },
    {
      size: 'sm',
      card: false,
      className: 'text-sm',
    },
    {
      size: 'md',
      card: false,
      className: 'text-sm laptop:text-lg',
    },
    {
      size: 'lg',
      card: false,
      className: 'text-xl',
    },
    {
      size: 'sm',
      card: true,
      className: 'text-xs',
    },
    {
      size: 'md',
      card: true,
      className: 'text-sm',
    },
    {
      size: 'lg',
      card: true,
      className: 'text-md',
    },
    {
      strong: true,
      card: true,
      className: 'font-normal',
    },
    {
      card: true,
      labelOrientation: ['top', 'bottom'],
      className: 'gap-[2px]',
    },
  ],
  defaultVariants: {
    labelOrientation: 'right',
    size: 'sm',
    align: 'left',
    spacing: 'none',
    strong: false,
    card: false,
  },
});

const numberSize = cva('', {
  variants: {
    labelOrientation: {
      top: '',
      right: '',
      bottom: '',
      left: '',
      none: '',
    },
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
    },
  },
  compoundVariants: [
    {
      labelOrientation: ['top', 'bottom'],
      size: 'sm',
      className: 'lg',
    },
    {
      labelOrientation: ['top', 'bottom'],
      size: 'md',
      className: 'xl-responsive',
    },
    {
      labelOrientation: ['top', 'bottom'],
      size: 'lg',
      className: 'xxxl',
    },
    {
      labelOrientation: ['left', 'right'],
      size: 'sm',
      className: 'sm',
    },
    {
      labelOrientation: ['left', 'right'],
      size: 'md',
      className: 'md',
    },
    {
      labelOrientation: ['left', 'right'],
      size: 'lg',
      className: 'lg',
    },
  ],
  defaultVariants: {
    labelOrientation: 'right',
    size: 'sm',
  },
});

const comingSoonStyle = cva('italic text-neutral-700 antialiased', {
  variants: {
    labelOrientation: {
      top: '',
      right: '',
      bottom: '',
      left: '',
      none: '',
    },
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
    },
  },
  compoundVariants: [
    {
      labelOrientation: ['top', 'bottom'],
      size: 'sm',
      className: 'text-base',
    },
    {
      labelOrientation: ['top', 'bottom'],
      size: 'md',
      className: 'text-lg',
    },
    {
      labelOrientation: ['top', 'bottom'],
      size: 'lg',
      className: 'text-xl',
    },
  ],
  defaultVariants: {
    labelOrientation: 'right',
    size: 'sm',
  },
});

const labelStyle = cva('antialiased', {
  variants: {
    labelOrientation: {
      top: '',
      right: '',
      bottom: '',
      left: '',
      none: 'hidden',
    },
    strong: {
      true: 'text-neutral-900',
      false: 'text-neutral-900',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
  defaultVariants: {
    strong: false,
  },
});

export interface LabeledNumberBaseProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'onClick'>,
    VariantProps<typeof labeledNumberContainer> {
  value: number | string | null | undefined;
  label?: string;
  secondaryLabel?: string;
  onClick?: () => void;
  variant?: FormattedNumberProps['variant'];
  fallbackLabel?: string;
  comingSoon?: boolean;
  icon?: ReactNode;
  showTooltip?: boolean;
  tooltipContent?: string;
}

export const LabeledNumberBase = forwardRef<
  HTMLDivElement,
  LabeledNumberBaseProps
>(
  (
    {
      value,
      label,
      secondaryLabel,
      labelOrientation = 'right',
      size,
      className,
      align,
      spacing,
      strong,
      onClick,
      variant,
      fallbackLabel,
      card,
      comingSoon,
      icon,
      showTooltip,
      tooltipContent,
      ...props
    }: LabeledNumberBaseProps,
    ref
  ) => {
    const ariaLabel = useMemo(() => {
      if (comingSoon) {
        return `${label ?? fallbackLabel}, coming soon`;
      }

      if (labelOrientation === 'top' || labelOrientation === 'left') {
        return `${label ?? fallbackLabel} ${value || 'None'} ${secondaryLabel}`;
      }

      return `${value || 'No'} ${label ?? fallbackLabel}`;
    }, [
      comingSoon,
      fallbackLabel,
      label,
      labelOrientation,
      secondaryLabel,
      value,
    ]);

    return (
      <div
        aria-label={ariaLabel}
        {...props}
        className={cn(
          labeledNumberContainer({
            labelOrientation,
            size,
            align,
            spacing,
            strong,
            card,
          }),
          className
        )}
        ref={ref}
        title={ariaLabel}
      >
        {[
          comingSoon ? (
            <span
              key="number"
              className={comingSoonStyle({ labelOrientation, size })}
            >
              Coming soon
            </span>
          ) : (
            <div className="flex items-center gap-1">
              {icon}
              <FormattedNumber
                key="number"
                value={value}
                variant={variant}
                onClick={onClick}
                label={secondaryLabel}
                strong={strong}
                size={
                  numberSize({
                    labelOrientation,
                    size,
                  }) as FormattedNumberProps['size']
                }
              />
            </div>
          ),
          <span
            key="label"
            className={`${labelStyle({
              labelOrientation,
              strong,
              align,
            })} flex`}
          >
            {label ?? fallbackLabel}
            {showTooltip && (
              <Tooltip content={tooltipContent || 'Tooltip Placeholder'}>
                <button
                  aria-label="Add"
                  className="ml-2 w-[20px]"
                  type="button"
                >
                  <InfoIcon />
                </button>
              </Tooltip>
            )}
          </span>,
        ].sort(() =>
          labelOrientation === 'left' || labelOrientation === 'top' ? -1 : 1
        )}
      </div>
    );
  }
);

LabeledNumberBase.displayName = 'LabeledNumberBase';
